import {
  Close,
  Information,
  View,
  WarningAltFilled,
} from '@carbon/icons-react';
import * as React from 'react';
import {
  Body,
  Button,
  Icon,
  IconComponentType,
  Tooltip,
} from 'vet-bones/design-system/components';

import {
  TEST_IDS,
  getProjectResourceCardDescriptionTestId,
  getProjectResourceCardRemoveButtonTestId,
  getProjectResourceCardViewPreviewButtonTestId,
} from 'src/constants/testIds';
import * as copy from 'src/templates/copy';
import { FileCreationMetadata } from 'src/types/fileUpload';

import styles from 'src/containers/ProjectNewResourceDialog/components/projectResourceCard.module.scss';

export type ProjectResourceCardProps = {
  resourceName: string;
  resourceIcon: IconComponentType;
  fileId: string;
  index: number;
  isPartial?: boolean;
  metadata?: FileCreationMetadata;
  validationError?: string;
  onViewPreview: (index: number) => void;
  onRemove: (index: number) => void;
};

export const ProjectResourceCard: React.VFC<ProjectResourceCardProps> = ({
  resourceName,
  resourceIcon,
  fileId,
  index,
  isPartial,
  metadata,
  validationError,
  onViewPreview,
  onRemove,
}) => {
  // Metadata that settled with no fields means the parse never succeeded —
  // there is nothing to describe, and the warning icon carries the why.
  const description = metadata?.fields.length
    ? copy.components.projectResourceDialog.resourceTypePanel.projectResourceCard.description(
        metadata.fields.length,
        metadata.cachedFileStats?.totalRows ?? metadata.rowCount,
        metadata.fields.map((f) => f.label).join(', ')
      )
    : null;

  return (
    <div className={styles.projectResourceCard}>
      <div className={styles.header}>
        <div className={styles.icon}>
          <Icon IconComponent={resourceIcon} size="md" />
        </div>
        <div className={styles.content}>
          <Body ellipsize size="lg">
            {resourceName}
          </Body>
        </div>
        <div className={styles.actions}>
          {isPartial ? (
            <Tooltip description={copy.notifications.csv.partial}>
              <div
                className={styles.partialIcon}
                data-testid={TEST_IDS.projectResourceDialog.partialIcon}
              >
                <Icon IconComponent={Information} size="md" />
              </div>
            </Tooltip>
          ) : null}
          {validationError ? (
            <Tooltip description={validationError}>
              <div
                className={styles.errorIcon}
                data-testid={TEST_IDS.projectResourceDialog.errorIcon}
              >
                <Icon IconComponent={WarningAltFilled} size="md" />
              </div>
            </Tooltip>
          ) : null}
          <Tooltip
            description={
              copy.components.projectResourceDialog.resourceTypePanel
                .projectResourceCard.viewPreview
            }
          >
            <Button
              leftIcon={View}
              onClick={() => onViewPreview(index)}
              size="xs"
              testId={getProjectResourceCardViewPreviewButtonTestId(
                fileId,
                index
              )}
              variant="tertiary-gray"
            />
          </Tooltip>
          <Tooltip
            description={
              copy.components.projectResourceDialog.resourceTypePanel
                .projectResourceCard.removeFile
            }
          >
            <Button
              leftIcon={Close}
              onClick={() => onRemove(index)}
              size="xs"
              testId={getProjectResourceCardRemoveButtonTestId(fileId, index)}
              variant="tertiary-gray"
            />
          </Tooltip>
        </div>
      </div>
      {description ? (
        <Tooltip description={description}>
          <Body
            className={styles.description}
            size="md"
            testId={getProjectResourceCardDescriptionTestId(fileId, index)}
          >
            {description}
          </Body>
        </Tooltip>
      ) : null}
    </div>
  );
};
